import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { format, startOfDay } from "date-fns";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
  useFormContext,
} from "react-hook-form";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { CalendarIcon, ChevronDown } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { useEffect, useState, useMemo } from "react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FacilitySchedule } from "@/types/facility/schedule";
import { FormControl } from "@/components/ui/form";
import { useGetFacilitiesQuery } from "@/services/facilityApis";
import Combobox from "@/components/ui/common/combobox";
import { useGetIncomeCategoryQuery } from "@/services/incomeCategoryApi";
import { useGetTaxGroupQuery } from "@/services/salesTaxApi";
import { TaxGroupType, FacilityType } from "@/types/enums";
import { Eye } from "lucide-react";
import { useGetPriceMastersQuery } from "@/services/priceMasterApi";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import PriceMasterForm from "../../price_master/modal/form";
import { PriceMaster } from "@/types/system-setting/priceMaster";
import RecurrenceSelector from "./recurrence-selector";

interface FacilityScheduleGeneralInfoFormProps {
  control: Control<FacilitySchedule>;
  errors: FieldErrors<FacilitySchedule>;
  watch: UseFormWatch<FacilitySchedule>;
  setValue: UseFormSetValue<FacilitySchedule>;
  trigger?: UseFormTrigger<FacilitySchedule>;
  action: "add" | "edit";
  open: boolean;
  /** Date from calendar click – auto-fills Start Date when creating */
  selectedDate?: Date;
}

const colorOptions = [
  // Row 1
  { value: "red-500", label: "Red", class: "bg-red-500" },
  { value: "orange-500", label: "Orange", class: "bg-orange-500" },
  { value: "yellow-500", label: "Yellow", class: "bg-yellow-500" },
  { value: "green-500", label: "Green", class: "bg-green-500" },
  { value: "teal-500", label: "Teal", class: "bg-teal-500" },
  { value: "blue-500", label: "Blue", class: "bg-blue-500" },
  { value: "purple-500", label: "Purple", class: "bg-purple-500" },
  // Row 2
  { value: "red-600", label: "Dark Red", class: "bg-red-600" },
  { value: "orange-600", label: "Dark Orange", class: "bg-orange-600" },
  { value: "yellow-600", label: "Dark Yellow", class: "bg-yellow-600" },
  { value: "green-600", label: "Dark Green", class: "bg-green-600" },
  { value: "teal-600", label: "Dark Teal", class: "bg-teal-600" },
  { value: "blue-600", label: "Dark Blue", class: "bg-blue-600" },
  { value: "purple-600", label: "Dark Purple", class: "bg-purple-600" },
  // Row 3
  { value: "red-700", label: "Darker Red", class: "bg-red-700" },
  { value: "orange-700", label: "Darker Orange", class: "bg-orange-700" },
  { value: "yellow-700", label: "Darker Yellow", class: "bg-yellow-700" },
  { value: "green-700", label: "Darker Green", class: "bg-green-700" },
  { value: "teal-700", label: "Darker Teal", class: "bg-teal-700" },
  { value: "blue-700", label: "Darker Blue", class: "bg-blue-700" },
  { value: "purple-700", label: "Darker Purple", class: "bg-purple-700" },
];

const GeneralInfoForm = ({
  control,
  errors,
  watch,
  setValue,
  action,
  open,
  selectedDate,
}: FacilityScheduleGeneralInfoFormProps & { selectedDate?: Date }) => {
  const {
    control: contextControl,
    formState: { errors: contextErrors },
    watch: contextWatch,
    setValue: contextSetValue,
    trigger: contextTrigger,
  } = useFormContext<FacilitySchedule>();

  const formControl = contextControl || control;
  const formErrors = contextErrors || errors;
  const formWatch = contextWatch || watch;
  const formSetValue = contextSetValue || setValue;
  const formTrigger = contextTrigger;

  const isEditMode = action === "edit";

  const watcher = formWatch();
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const { data: facilityDataList } = useGetFacilitiesQuery({
    query:
      "status=active&bookable=true&load_add_ons=true&is_add_on=false&sort_key=id&sort_order=desc",
    orgId: orgId,
  });

  const { data: incomeCatData } = useGetIncomeCategoryQuery(
    { org_id: orgId, query: "sort_key=id&sort_order=desc&status=active" },
    { skip: !orgId && !open }
  );

  const { data: taxGroupData } = useGetTaxGroupQuery(
    { query: "", org_id: orgId },
    { skip: !orgId && !open }
  );

  const { data: priceMasterData, isLoading: priceMasterLoading } =
    useGetPriceMastersQuery(
      { query: "item_type=facility_slot", org_id: orgId },
      { skip: !orgId || !open }
    );

  const startTime = formWatch("general_info.start_time");
  const endTime = formWatch("general_info.end_time");
  const durationPerSlot = formWatch("general_info.duration_per_slot");

  const totalTimeRangeHours = useMemo(() => {
    if (!startTime || !endTime) return 0;
    const [startH, startM] = startTime.split(":").map(Number);
    const [endH, endM] = endTime.split(":").map(Number);

    const startTotalMinutes = startH * 60 + startM;
    const endTotalMinutes = endH * 60 + endM;

    let diffMinutes = endTotalMinutes - startTotalMinutes;
    if (diffMinutes <= 0) diffMinutes += 24 * 60; // Handle overnight range

    return diffMinutes / 60;
  }, [startTime, endTime]);

  const isBreakDisabled = useMemo(() => {
    if (isEditMode) return true;
    if (!totalTimeRangeHours || !durationPerSlot) return false;

    // If total diff is equal to or less than duration per slot, disable break
    return totalTimeRangeHours <= durationPerSlot;
  }, [totalTimeRangeHours, durationPerSlot, isEditMode]);

  // Reset break duration if it becomes disabled
  useEffect(() => {
    if (isBreakDisabled && !isEditMode) {
      formSetValue("general_info.break_duration", 0);
    }
  }, [isBreakDisabled, formSetValue, isEditMode]);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedRuleData, setSelectedRuleData] = useState<PriceMaster | null>(
    null
  );

  const shouldHidePricingFields = useMemo(() => {
    const selectedFacilityId = formWatch("general_info.facility_id");
    if (selectedFacilityId && facilityDataList?.data) {
      const facility = facilityDataList.data.find(
        (f) => f.id === selectedFacilityId
      );
      return (
        facility?.facility_type === FacilityType.Trainer ||
        facility?.credit_only === true
      );
    }
    return false;
  }, [formWatch("general_info.facility_id"), facilityDataList]);

  const incomeCatOptions = useMemo(() => {
    return Array.isArray(incomeCatData?.data)
      ? incomeCatData.data.map((incomeCat) => ({
          ...incomeCat,
          label: incomeCat?.name,
          value: incomeCat?.id?.toString() || "",
        }))
      : [];
  }, [incomeCatData]);

  const taxGroupOptions = useMemo(() => {
    return Array.isArray(taxGroupData?.data)
      ? taxGroupData.data
          .filter((tg) => tg.type === TaxGroupType.Item)
          .map((taxGroup) => ({
            ...taxGroup,
            label: taxGroup?.name,
            value: taxGroup?.id?.toString() || "",
          }))
      : [];
  }, [taxGroupData]);

  const facilityOptions = useMemo(() => {
    return Array.isArray(facilityDataList?.data)
      ? facilityDataList.data.map((facility) => ({
          ...facility,
          label: facility.name || "",
          value: facility.id?.toString() || "",
        }))
      : [];
  }, [facilityDataList]);

  const priceMasterOptions = useMemo(() => {
    return priceMasterData && Array.isArray((priceMasterData as any).data)
      ? (priceMasterData as any).data.map((rule: any) => ({
          label: `${rule.rule_code} : ${rule.rule_name}`,
          subLabel: rule.rule_name,
          value: String(rule.id),
        }))
      : [];
  }, [priceMasterData]);

  const handlePreview = (ruleId: number) => {
    const rule = (priceMasterData as any)?.data?.find(
      (r: any) => r.id === ruleId
    );
    if (rule) {
      setSelectedRuleData(rule);
      setPreviewOpen(true);
    }
  };

  const selectedFacilityCapacity = useMemo(() => {
    const selectedFacilityId = formWatch("general_info.facility_id");
    if (selectedFacilityId && facilityDataList?.data) {
      const facility = facilityDataList.data.find(
        (f) => f.id === selectedFacilityId
      );
      return facility?.max_capacity || null;
    }
    return null;
  }, [formWatch("general_info.facility_id"), facilityDataList]);

  const selectedFacilityBookingInfo = useMemo(() => {
    const selectedFacilityId = formWatch("general_info.facility_id");
    if (selectedFacilityId && facilityDataList?.data) {
      const facility = facilityDataList.data.find(
        (f) => f.id === selectedFacilityId
      );
      return {
        bookingType: facility?.booking_type,
        isMemberOnlyFacility: facility?.booking_type === "Member Only",
      };
    }
    return { bookingType: null, isMemberOnlyFacility: false };
  }, [formWatch("general_info.facility_id"), facilityDataList]);

  useEffect(() => {
    const subscription = formWatch((value, { name }) => {
      if (name === "general_info.facility_id") {
        const facilityId = Number(value?.general_info?.facility_id);
        if (facilityId && facilityDataList?.data) {
          const facility = facilityDataList.data.find(
            (f) => f.id === facilityId
          );

          // Only auto-fill duration_per_slot in add mode
          if (!isEditMode && facility && facility.duration_per_slot) {
            formSetValue(
              "general_info.duration_per_slot",
              facility.duration_per_slot
            );
          }

          // Reset max_attendees if it exceeds the new facility's capacity
          // Only in add mode — in edit mode attendees are already set from API
          if (!isEditMode) {
            const currentMaxAttendees = value?.general_info?.max_attendees;
            if (
              facility?.max_capacity &&
              currentMaxAttendees &&
              currentMaxAttendees > facility.max_capacity
            ) {
              formSetValue(
                "general_info.max_attendees",
                facility.max_capacity
              );
            }
          }

          // member_only: sync with facility booking type always
          if (facility?.booking_type === "Member Only") {
            formSetValue("general_info.member_only", true);
          } else if (!isEditMode) {
            // In edit mode keep the saved value; in add mode reset to false
            formSetValue("general_info.member_only", false);
          }
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [formWatch, formSetValue, facilityDataList, isEditMode]);

  useEffect(() => {
    if (formTrigger && selectedFacilityCapacity) {
      formTrigger("general_info.max_attendees");
    }
  }, [selectedFacilityCapacity, formTrigger]);

  // Pre-fill start_date from calendar click (only in add mode)
  useEffect(() => {
    if (selectedDate && action === "add") {
      formSetValue(
        "general_info.start_date",
        format(selectedDate, "yyyy-MM-dd")
      );
    }
  }, [selectedDate, action]);

  // Trigger duration_per_slot validation when time range changes
  useEffect(() => {
    if (formTrigger && (startTime || endTime)) {
      formTrigger("general_info.duration_per_slot");
      if (isEditMode) {
        formTrigger("general_info.start_time");
        formTrigger("general_info.end_time");
      }
    }
  }, [startTime, endTime, formTrigger, isEditMode]);

  const validateTimeRangeExpansion = (start: string | undefined, end: string | undefined) => {
    if (isEditMode && start && end && durationPerSlot) {
      const [sH, sM] = start.split(":").map(Number);
      const [eH, eM] = end.split(":").map(Number);
      let diff = eH * 60 + eM - (sH * 60 + sM);
      if (diff <= 0) diff += 1440;
      if (diff / 60 > durationPerSlot) {
        return `Range cannot exceed slot duration (${durationPerSlot}h)`;
      }
    }
    return true;
  };

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <p className="font-medium text-sm">Facility Booking Details</p>
        <Separator className="h-[1px]" />

        {/* ── Row 1 ── */}
         <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
          {/* Facility */}
          <div>
            <Controller
              name="general_info.facility_id"
              rules={{
                validate: (value) =>
                  (value !== undefined && value !== null && value !== 0) ||
                  "Required",
              }}
              control={formControl}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <p className="text-sm">
                    Facility <span className="text-red-500">*</span>
                  </p>
                  <Combobox
                    list={facilityOptions}
                    defaultValue={
                      facilityOptions?.find(
                        (option) => option.value === value?.toString()
                      )?.label || ""
                    }
                    setFilter={(selectedLabel: string) => {
                      const selectedOption = facilityOptions?.find(
                        (option) => option.label === selectedLabel
                      );
                      onChange(
                        selectedOption
                          ? Number(selectedOption.value)
                          : undefined
                      );
                      // ✅ Do NOT auto-fill max_attendees here —
                      // useEffect subscription handles it only in add mode
                    }}
                    label="Facility"
                    showLabel={false}
                    disabled={isEditMode}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>

          {/* Members Only toggle */}
          <div className="flex flex-col justify-center">
            <div className="flex items-center gap-2 mt-2">
              <Controller
                control={formControl}
                name="general_info.member_only"
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={selectedFacilityBookingInfo.isMemberOnlyFacility}
                  />
                )}
              />
              <span
                className={`text-sm ${
                  selectedFacilityBookingInfo.isMemberOnlyFacility
                    ? "text-gray-500"
                    : ""
                }`}
              >
                Members Only
              </span>
            </div>
          </div>

          {/* Colour */}
          <div>
            <Controller
              control={formControl}
              name="general_info.color"
              rules={{ required: "Required" }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <div>
                  <p className="text-sm">
                    Colour <span className="text-red-500">*</span>
                  </p>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-between">
                        <div className="flex items-center">
                          {value && (
                            <div
                              className={`w-4 h-4 rounded-full mr-2 ${
                                colorOptions.find((c) => c.value === value)?.class
                              }`}
                            />
                          )}
                          <span className="font-normal text-sm">
                            {value
                              ? colorOptions.find((c) => c.value === value)?.label
                              : "Select Colour"}
                          </span>
                        </div>
                        <ChevronDown className="h-4 w-4 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-3" align="start">
                      <div className="space-y-2">
                        <h4 className="font-medium text-sm">Set Colour</h4>
                        <div className="grid grid-cols-7 gap-2">
                          {colorOptions.map((color, index) => (
                            <button
                              key={index}
                              type="button"
                              className={cn(
                                "w-6 h-6 rounded-full",
                                color.class,
                                value === color.value &&
                                  "ring-2 ring-offset-2 ring-black"
                              )}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onChange(color.value);
                              }}
                              title={color.label}
                            />
                          ))}
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                  {error && (
                    <span className="text-red-500 text-xs mt-[5px]">
                      {error.message}
                    </span>
                  )}
                </div>
              )}
            />
          </div>

          {/* Max Attendees */}
          <div>
            <p className="text-sm">
              Max Attendees <span className="text-red-500">*</span>
            </p>
            <Controller
              control={formControl}
              name="general_info.max_attendees"
              rules={{
                required: "Required",
                min: { value: 1, message: "Minimum attendees is 1" },
                validate: (value) => {
                  if (
                    selectedFacilityCapacity &&
                    value &&
                    value > selectedFacilityCapacity
                  ) {
                    return `Max Attendees cannot exceed the facility capacity of ${selectedFacilityCapacity}.`;
                  }
                  return true;
                },
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <Input
                    type="number"
                    id="general_info.max_attendees"
                    step="1"
                    min={1}
                    placeholder="Enter People"
                    value={value || ""}
                    onChange={(e) => onChange(Number(e.target.value))}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-1 block">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>
        </div>

        {/* ── Row 2 ── */}
        {/* Normal facility: all fields in one row (original layout) */}
        {/* credit_only / trainer: Location + Description row, then Advance Booking Days + dates row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 my-3">
          {/* Location */}
          <div>
            <p className="text-sm font-normal">Location</p>
            <Controller
              control={formControl}
              name="general_info.location"
              rules={{
                maxLength: {
                  value: 200,
                  message: "Location cannot exceed 200 characters",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Textarea
                    placeholder="Enter Location"
                    className="resize-none"
                    {...field}
                    rows={4}
                    value={field.value ?? ""}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-1 block">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>

          {/* Description */}
          <div>
            <p className="text-sm font-normal">Description</p>
            <Controller
              control={formControl}
              name="general_info.description"
              rules={{
                maxLength: {
                  value: 1000,
                  message: "Description cannot exceed 1000 characters",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Textarea
                    placeholder="Type your description here"
                    className="resize-none"
                    {...field}
                    rows={4}
                    value={field.value ?? ""}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-1 block">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>

          {/* Income Category + Tax Group — shown for normal facilities */}
          {!shouldHidePricingFields && (
            <>
              <div>
                <Controller
                  name="general_info.income_category_id"
                  rules={{
                    validate: (value) =>
                      (value !== undefined && value !== null && value !== 0) ||
                      "Required",
                  }}
                  control={formControl}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <>
                      <p className="text-sm">
                        Income Category <span className="text-red-500">*</span>
                      </p>
                      <Combobox
                        list={incomeCatOptions}
                        defaultValue={
                          incomeCatOptions?.find(
                            (option) => option.value === value?.toString()
                          )?.label || ""
                        }
                        setFilter={(selectedLabel: string) => {
                          const selectedOption = incomeCatOptions?.find(
                            (option) => option.label === selectedLabel
                          );
                          onChange(
                            selectedOption
                              ? Number(selectedOption.value)
                              : undefined
                          );
                        }}
                        label="Income Category"
                        showLabel={false}
                      />
                      {error && (
                        <span className="text-red-500 text-xs mt-[5px]">
                          {error.message}
                        </span>
                      )}
                    </>
                  )}
                />
              </div>

              {/* Tax Group */}
              <div>
                <Controller
                  name="general_info.tax_group_id"
                  rules={{
                    validate: (value) =>
                      (value !== undefined && value !== null && value !== 0) ||
                      "Required",
                  }}
                  control={formControl}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <>
                      <p className="text-sm">
                        Tax Group <span className="text-red-500">*</span>
                      </p>
                      <Combobox
                        list={taxGroupOptions}
                        defaultValue={
                          taxGroupOptions?.find(
                            (option) => option.value === value?.toString()
                          )?.label || ""
                        }
                        setFilter={(selectedLabel: string) => {
                          const selectedOption = taxGroupOptions?.find(
                            (option) => option.label === selectedLabel
                          );
                          onChange(
                            selectedOption
                              ? Number(selectedOption.value)
                              : undefined
                          );
                        }}
                        label="Tax Group"
                        showLabel={false}
                      />
                      {error && (
                        <span className="text-red-500 text-xs mt-[5px]">
                          {error.message}
                        </span>
                      )}
                    </>
                  )}
                />
              </div>
            </>
          )}

          {/* Advance Booking Days — in same row for normal facilities (original layout) */}
          {!shouldHidePricingFields && (
            <div>
              <p className="text-sm">
                Advance Booking Days <span className="text-red-500">*</span>
              </p>
              <Controller
                control={formControl}
                name="general_info.advance_booking_days"
                rules={{
                  required: "Required",
                  min: {
                    value: 1,
                    message: "Minimum advance booking days is 1",
                  },
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <>
                    <Input
                      type="number"
                      step="1"
                      placeholder="Enter Booking Days"
                      value={value || ""}
                      onChange={onChange}
                    />
                    {error && (
                      <span className="text-red-500 text-xs mt-1 block">
                        {error.message}
                      </span>
                    )}
                  </>
                )}
              />
            </div>
          )}

          {/* Start Date — in same row for normal facilities (original layout), disabled in edit mode */}
          {!shouldHidePricingFields && (
            <div className="flex flex-col justify-end">
              <span className="p-0 text-sm text-black left-2 -top-1.5 px-1 bg-white">
                Start Date <span className="text-red-500">*</span>
              </span>
              <Controller
                name={"general_info.start_date" as keyof FacilitySchedule}
                rules={{
                  required: "Required",
                  validate: (value) => {
                    if (action === "add") {
                      const selectedDate = new Date(value as string);
                      const today = startOfDay(new Date());
                      if (selectedDate < today) {
                        return "Start date cannot be in the past";
                      }
                    }
                    return true;
                  },
                }}
                control={formControl}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Popover>
                    <PopoverTrigger asChild disabled={isEditMode}>
                      <FormControl>
                        <div className="relative">
                          <Button
                            variant="outline"
                            type="button"
                            disabled={isEditMode}
                            className={cn(
                              "w-full pl-3 text-gray-800 text-left font-normal hover:bg-transparent border-[1px]",
                              isEditMode && "opacity-60 cursor-not-allowed"
                            )}
                          >
                            {value ? (
                              format(
                                new Date(value as unknown as string),
                                "dd-MM-yyyy"
                              )
                            ) : (
                              <span>Select start date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                          {error && (
                            <span className="text-red-500 text-xs mt-[5px]">
                              {error.message}
                            </span>
                          )}
                        </div>
                      </FormControl>
                    </PopoverTrigger>
                    {!isEditMode && (
                      <PopoverContent className="w-auto p-0" align="center">
                        <Calendar
                          mode="single"
                          captionLayout="dropdown-buttons"
                          selected={
                            value
                              ? new Date(value as unknown as string)
                              : undefined
                          }
                          defaultMonth={new Date()}
                          onSelect={(selectedDate) => {
                            if (selectedDate) {
                              onChange(format(selectedDate, "yyyy-MM-dd"));
                            }
                          }}
                          disabled={(date) =>
                            date.getTime() < new Date().setHours(0, 0, 0, 0)
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    )}
                  </Popover>
                )}
              />
            </div>
          )}

          {/* Start Time — in same row for normal facilities (original layout) */}
          {!shouldHidePricingFields && (
            <div className="flex flex-col justify-end">
              <span className="p-0 text-sm text-black left-2 -top-1.5 px-1 bg-white">
                Start Time <span className="text-red-500">*</span>
              </span>
              <Controller
                name="general_info.start_time"
                rules={{
                  required: "Required",
                  validate: (value) => {
                    const endTime = formWatch("general_info.end_time");
                    return validateTimeRangeExpansion(value, endTime);
                  },
                }}
                control={formControl}
                render={({ field, fieldState: { error } }) => (
                  <div className="relative">
                    <Input
                      {...field}
                      type="time"
                      step="60"
                      placeholder="Select start time"
                      className={cn(
                        "w-full h-10 border-[1px] focus-within:ring-0",
                        error && "border-red-500"
                      )}
                      value={field.value ?? ""}
                    />
                    {error && (
                      <span className="text-red-500 text-xs mt-1 block">
                        {error.message}
                      </span>
                    )}
                  </div>
                )}
              />
            </div>
          )}

          {/* End Time — in same row for normal facilities (original layout) */}
          {!shouldHidePricingFields && (
            <div className="flex flex-col justify-end">
              <span className="p-0 text-sm text-black left-2 -top-1.5 px-1 bg-white">
                End Time <span className="text-red-500">*</span>
              </span>
              <Controller
                name="general_info.end_time"
                rules={{
                  required: "Required",
                  validate: (value) => {
                    const startTime = formWatch("general_info.start_time");
                    if (startTime && value === startTime) {
                      return "End time cannot be same as start time";
                    }
                    return validateTimeRangeExpansion(startTime, value);
                  },
                }}
                control={formControl}
                render={({ field, fieldState: { error } }) => (
                  <div className="relative">
                    <Input
                      {...field}
                      type="time"
                      step="60"
                      placeholder="Select end time"
                      className={cn(
                        "w-full h-10 border-[1px] focus-within:ring-0",
                        error && "border-red-500"
                      )}
                      value={field.value ?? ""}
                    />
                    {error && (
                      <span className="text-red-500 text-xs mt-1 block">
                        {error.message}
                      </span>
                    )}
                  </div>
                )}
              />
            </div>
          )}
        </div>

        {/* ── Row 3 (credit_only / trainer only): Advance Booking Days, Start Date, Start Time, End Time ── */}
        {shouldHidePricingFields && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 my-3">
            {/* Advance Booking Days */}
            <div>
              <p className="text-sm">
                Advance Booking Days <span className="text-red-500">*</span>
              </p>
              <Controller
                control={formControl}
                name="general_info.advance_booking_days"
                rules={{
                  required: "Required",
                  min: {
                    value: 1,
                    message: "Minimum advance booking days is 1",
                  },
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <>
                    <Input
                      type="number"
                      step="1"
                      placeholder="Enter Booking Days"
                      value={value || ""}
                      onChange={onChange}
                    />
                    {error && (
                      <span className="text-red-500 text-xs mt-1 block">
                        {error.message}
                      </span>
                    )}
                  </>
                )}
              />
            </div>

            {/* Start Date */}
            <div className="flex flex-col justify-end">
              <span className="p-0 text-sm text-black left-2 -top-1.5 px-1 bg-white">
                Start Date <span className="text-red-500">*</span>
              </span>
              <Controller
                name={"general_info.start_date" as keyof FacilitySchedule}
                rules={{
                  required: "Required",
                  validate: (value) => {
                    if (action === "add") {
                      const selectedDate = new Date(value as string);
                      const today = startOfDay(new Date());
                      if (selectedDate < today) {
                        return "Start date cannot be in the past";
                      }
                    }
                    return true;
                  },
                }}
                control={formControl}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Popover>
                    <PopoverTrigger asChild disabled={isEditMode}>
                      <FormControl>
                        <div className="relative">
                          <Button
                            variant="outline"
                            type="button"
                            disabled={isEditMode}
                            className={cn(
                              "w-full pl-3 text-gray-800 text-left font-normal hover:bg-transparent border-[1px]",
                              isEditMode && "opacity-60 cursor-not-allowed"
                            )}
                          >
                            {value ? (
                              format(
                                new Date(value as unknown as string),
                                "dd-MM-yyyy"
                              )
                            ) : (
                              <span>Select start date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                          {error && (
                            <span className="text-red-500 text-xs mt-[5px]">
                              {error.message}
                            </span>
                          )}
                        </div>
                      </FormControl>
                    </PopoverTrigger>
                    {!isEditMode && (
                      <PopoverContent className="w-auto p-0" align="center">
                        <Calendar
                          mode="single"
                          captionLayout="dropdown-buttons"
                          selected={
                            value
                              ? new Date(value as unknown as string)
                              : undefined
                          }
                          defaultMonth={new Date()}
                          onSelect={(selectedDate) => {
                            if (selectedDate) {
                              onChange(format(selectedDate, "yyyy-MM-dd"));
                            }
                          }}
                          disabled={(date) =>
                            date.getTime() < new Date().setHours(0, 0, 0, 0)
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    )}
                  </Popover>
                )}
              />
            </div>

            {/* Start Time */}
            <div className="flex flex-col justify-end">
              <span className="p-0 text-sm text-black left-2 -top-1.5 px-1 bg-white">
                Start Time <span className="text-red-500">*</span>
              </span>
              <Controller
                name="general_info.start_time"
                rules={{
                  required: "Required",
                  validate: (value) => {
                    const endTime = formWatch("general_info.end_time");
                    return validateTimeRangeExpansion(value, endTime);
                  },
                }}
                control={formControl}
                render={({ field, fieldState: { error } }) => (
                  <div className="relative">
                    <Input
                      {...field}
                      type="time"
                      step="60"
                      placeholder="Select start time"
                      className={cn(
                        "w-full h-10 border-[1px] focus-within:ring-0",
                        error && "border-red-500"
                      )}
                      value={field.value ?? ""}
                    />
                    {error && (
                      <span className="text-red-500 text-xs mt-1 block">
                        {error.message}
                      </span>
                    )}
                  </div>
                )}
              />
            </div>

            {/* End Time */}
            <div className="flex flex-col justify-end">
              <span className="p-0 text-sm text-black left-2 -top-1.5 px-1 bg-white">
                End Time <span className="text-red-500">*</span>
              </span>
              <Controller
                name="general_info.end_time"
                rules={{
                  required: "Required",
                  validate: (value) => {
                    const startTime = formWatch("general_info.start_time");
                    if (startTime && value === startTime) {
                      return "End time cannot be same as start time";
                    }
                    return validateTimeRangeExpansion(startTime, value);
                  },
                }}
                control={formControl}
                render={({ field, fieldState: { error } }) => (
                  <div className="relative">
                    <Input
                      {...field}
                      type="time"
                      step="60"
                      placeholder="Select end time"
                      className={cn(
                        "w-full h-10 border-[1px] focus-within:ring-0",
                        error && "border-red-500"
                      )}
                      value={field.value ?? ""}
                    />
                    {error && (
                      <span className="text-red-500 text-xs mt-1 block">
                        {error.message}
                      </span>
                    )}
                  </div>
                )}
              />
            </div>
          </div>
        )}

        {/* ── Row 3: Price Master, Duration, Break ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 my-3">
          {/* Price Master */}
          {!shouldHidePricingFields && (
            <div className="col-span-1">
              <Controller
                name="general_info.price_master_id"
                control={formControl}
                rules={{ required: "Required" }}
                render={({ field, fieldState: { error } }) => (
                  <div className="flex flex-col gap-1">
                    <p className="text-sm">
                      Price Master Rule <span className="text-red-500">*</span>
                    </p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-[150px]">
                        <Combobox
                          list={priceMasterOptions}
                          defaultValue={
                            priceMasterOptions.find(
                              (opt: any) => opt.value === String(field.value)
                            )?.label || ""
                          }
                          setFilter={(selectedLabel: string) => {
                            const selectedOption = priceMasterOptions.find(
                              (opt: any) => opt.label === selectedLabel
                            );
                            field.onChange(
                              selectedOption
                                ? Number(selectedOption.value)
                                : undefined
                            );
                          }}
                          label="Select Rule"
                          showLabel={false}
                          disabled={priceMasterLoading}
                        />
                      </div>
                      {field.value && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-9 w-9 text-indigo-600 hover:bg-indigo-50 border border-indigo-100 shrink-0"
                          onClick={() => handlePreview(Number(field.value))}
                          title="Preview details"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    {error && (
                      <span className="text-red-500 text-xs mt-1">
                        {error.message}
                      </span>
                    )}
                  </div>
                )}
              />
            </div>
          )}

          {/* Duration Per Slot — disabled in edit mode */}
          <div className="flex flex-col gap-1">
            <p className="text-sm">
              Duration Per Slot (Hours) <span className="text-red-500">*</span>
            </p>
            <Controller
              control={formControl}
              name="general_info.duration_per_slot"
              rules={{
                required: "Required",
                min: { value: 0.1, message: "Minimum duration is 0.1 hours" },
                validate: (value) => {
                  if (totalTimeRangeHours && value > totalTimeRangeHours) {
                    return `Slot duration cannot exceed the total time range of ${totalTimeRangeHours} hour(s)`;
                  }
                  return true;
                },
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <Input
                    type="number"
                    step="0.1"
                    min="0.1"
                    placeholder="Enter Hours"
                    value={value || ""}
                    onChange={(e) => onChange(Number(e.target.value))}
                    disabled={isEditMode}
                    className={cn(isEditMode && "opacity-60 cursor-not-allowed")}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-1 block">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>

          {/* Break Duration — disabled in edit mode */}
          <div className="flex flex-col gap-1">
            <p className="text-sm">Break Duration (Minutes)</p>
            <Controller
              control={formControl}
              name="general_info.break_duration"
              rules={{
                min: { value: 0, message: "Minimum break is 0" },
              }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <Input
                    type="number"
                    step="1"
                    min="0"
                    placeholder="Enter Minutes"
                    value={value || ""}
                    onChange={(e) => onChange(Number(e.target.value))}
                    disabled={isBreakDisabled}
                    className={cn(isBreakDisabled && "opacity-60 cursor-not-allowed")}
                  />
                  {error && (
                    <span className="text-red-500 text-xs mt-1 block">
                      {error.message}
                    </span>
                  )}
                </>
              )}
            />
          </div>
        </div>
      </Card>

      {/* Price Master Preview Modal */}
      <Sheet open={previewOpen} onOpenChange={setPreviewOpen}>
        <SheetContent className="overflow-y-auto max-w-full sm:max-w-[700px] w-full p-0">
          <SheetHeader className="p-6 border-b bg-indigo-50/50">
            <SheetTitle className="text-xl font-bold text-indigo-900">
              Pricing Rule Preview
            </SheetTitle>
            <SheetDescription className="text-indigo-600">
              Viewing pre-configured pricing rule details
            </SheetDescription>
          </SheetHeader>
          <div className="p-6">
            {selectedRuleData && (
              <PriceMasterForm
                isOpen={previewOpen}
                setOpen={setPreviewOpen}
                action="edit"
                data={selectedRuleData}
                readOnly={true}
                setData={() => {}}
                setAction={() => {}}
                refetch={() => {}}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Recurrence Card */}
      <Controller
        control={formControl}
        name="general_info.recurrence"
        rules={{
          validate: (val: any) => {
            if (!val || val.frequency === "choose") {
              return "Please select a recurrence frequency";
            }
            return true;
          },
        }}
        render={({ field, fieldState: { error } }) => (
          <div className="space-y-1">
            <RecurrenceSelector
              value={field.value}
              startDate={formWatch("general_info.start_date")}
              onChange={(config) => {
                field.onChange(config);
              }}
            />
            {error && (
              <span className="text-red-500 text-xs px-1">{error.message}</span>
            )}
          </div>
        )}
      />
    </div>
  );
};

export default GeneralInfoForm;